import type { FC } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress } from '@mui/material';
import { Form, Formik } from 'formik';

import { SECTION } from 'src/constants/section';
import type { DetailedFollowUpTicket } from 'src/types/followUp';
import type { SurveyDetail } from 'src/types/survey';
import { useUserContext } from 'src/components/app/context/UserContext';
import useUserPermissions from 'src/components/app/context/UserContext/useUserPermissions';
import SuccessSnackbar from 'src/components/common/Snackbar/SuccessSnackbar';
import { useSurveyDetailQuery } from 'src/components/survey/SurveySettings/api';

import ModalSection from '../ModalSection';

import CallHappenedInput from './Inputs/CallHappenedInput';
import FirstContactAtInput from './Inputs/FirstContactAtInput';
import RootCausesButtonGroup from './Inputs/RootCausesButtonGroup';
import SurveyTextInput from './Inputs/SurveyTextInput';
import ActionButtons from './ActionButtons';
import type { FormFieldKeys } from './constants';
import useFollowUpCallForm from './useFollowUpCallForm';
import useFollowUpCallMutation from './useFollowUpCallMutation';
import { validationSchema } from './validationSchema';

import { styles } from '../ticketDetail.styles';

const FollowUpCallSection: FC<{
  ticket: DetailedFollowUpTicket;
  survey?: SurveyDetail;
  refetchList: () => void;
}> = ({ ticket, survey, refetchList }) => {
  const { t } = useTranslation('followUp');
  const [extended, setExtended] = useState(true);
  const { mutateAsync, isLoading, isSuccess, reset } = useFollowUpCallMutation({
    ticket,
  });
  const {
    hasReadOnlyPermissionForModuleType,
    hasAdminPermissionForModuleType,
  } = useUserPermissions();
  const { user } = useUserContext();

  const isFollowUpOwner = user && ticket.followUpOwner === user.id;
  const disabled =
    ticket?.status === 'Closed' ||
    (hasReadOnlyPermissionForModuleType(SECTION.FOLLOW_UP, ticket.surveyId) &&
      !isFollowUpOwner &&
      !hasAdminPermissionForModuleType(SECTION.FOLLOW_UP, ticket.surveyId));
  const { data: surveyDetail } = useSurveyDetailQuery(survey?.id);
  const { initialValues, customFields } = useFollowUpCallForm(
    ticket,
    surveyDetail,
  );

  const onSubmit = (formValues: FormFieldKeys) => {
    if (ticket) {
      const formValueDate = new Date(formValues.firstContactAt ?? '');
      const isoDate = isNaN(formValueDate.getDate())
        ? new Date()
        : formValueDate;
      mutateAsync({
        ...formValues,
        firstContactAt: isoDate.toISOString(),
      }).then(() => refetchList());
    }
  };

  const isAnyRootCauseActive = useMemo(
    () =>
      survey &&
      survey.specificSettings.followUp.rootCauses.filter(
        (cause) => cause.isActive,
      ).length > 0,
    [survey],
  );

  return (
    <ModalSection
      header={t('followUpCall.header')}
      extendable={{
        extended,
        toggleExtended: () => setExtended((prev) => !prev),
      }}
    >
      {isLoading || !ticket || !survey || !initialValues || !customFields ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={styles.modalSection}>
          <Formik
            validationSchema={validationSchema(t)}
            initialValues={initialValues}
            onSubmit={onSubmit}
          >
            <Form>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <input hidden defaultValue={ticket.npsGroup} name="npsGroup" />
                <CallHappenedInput disabled={disabled} />
                <FirstContactAtInput disabled={disabled} />
                {isAnyRootCauseActive && (
                  <RootCausesButtonGroup
                    disabled={disabled}
                    allRootCauses={[
                      ...survey.specificSettings.followUp.rootCauses,
                    ]}
                  />
                )}
                {customFields?.map((field, index) => (
                  <SurveyTextInput
                    key={field.id}
                    disabled={disabled}
                    field={field}
                    index={index}
                  />
                ))}
                <ActionButtons disabled={disabled} />
              </Box>
            </Form>
          </Formik>
        </Box>
      )}

      <SuccessSnackbar
        open={isSuccess}
        onClose={reset}
        title={t('comments.changesSaved')}
        message={t('comments.informationSaved')}
      />
    </ModalSection>
  );
};

export default FollowUpCallSection;
